import React, { type ReactNode, useCallback } from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';
import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { LinkMenuItem as MenuLinkItemPrimitive } from '@atlaskit/side-nav-items/link-menu-item';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import { useL1ItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	GlobalShortcutActionsItem,
	GlobalShortcutConfiguration,
	GlobalShortcutLinkItem,
} from '../../types';
import { ElementEventListener } from '../../utils/element-event-listener';
import { useDragAndDropOptions } from '../../utils/use-drag-and-drop-options';
import { MenuLoadingItem } from '../menu-loading-item';
import { ShortcutActionMenu } from '../shortcut-action';

import { messages } from './messages';

export type MenuLinkItemProps = Omit<GlobalShortcutLinkItem, 'ref' | 'type'> & {
	actions?: GlobalShortcutActionsItem[];
	ariaLabel?: string;
	children?: ReactNode;
	disabled?: boolean;
	isReorderable?: boolean;
	isSelected?: boolean;
	loading?: boolean;
	onDisable?: (reason?: string) => void;
	openInSameTab?: boolean;
	optionalRef?: GlobalShortcutConfiguration['ref'];
	product?: string;
	url?: string;
};

export type MenuLinkItemDraggableProps = Omit<MenuLinkItemProps, 'menuItemId'> & {
	actions?: GlobalShortcutActionsItem[];
	ariaLabel?: string;
	isReorderable?: boolean;
	menuItemId: string;
	url: string;
};

export const fireMenuLinkItemClickAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
): void => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'globalNavigation',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

/**
 * Utility to return whether actions and options should be shown
 */
const getShouldShowActionsAndOptions = (
	actions?: GlobalShortcutActionsItem[],
	optionsMenuElem?: ReactNode,
) => (actions && actions.length >= 0) || !!optionsMenuElem;

/**
 * Actions and Options component to render both the action menu and options menu
 * beside each other on hover/focus
 */
const ActionsAndOptions = ({
	actions,
	analyticsName,
	optionsMenuElem,
}: {
	actions?: GlobalShortcutActionsItem[];
	analyticsName?: string;
	optionsMenuElem?: ReactNode;
}) => (
	<>
		{actions && actions.length && (
			<ShortcutActionMenu actions={actions} analyticsName={analyticsName} />
		)}
		{optionsMenuElem}
	</>
);

const MenuLinkItemComponent = ({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	optionalRef,
	isSelected,
	openInSameTab,
	optionsMenuElem,
	analyticsName,
}: MenuLinkItemProps) => {
	const { formatMessage } = useIntl();
	const mergedRefs = mergeRefs(optionalRef ? [optionalRef] : []);
	const shouldShowActionsAndOptions = getShouldShowActionsAndOptions(actions, optionsMenuElem);

	return (
		<MenuLinkItemPrimitive
			actionsOnHover={
				shouldShowActionsAndOptions && (
					<ActionsAndOptions
						actions={actions}
						analyticsName={analyticsName}
						optionsMenuElem={optionsMenuElem}
					/>
				)
			}
			elemBefore={icon}
			elemAfter={
				!openInSameTab && (
					fg('navx-4022-gas-icons-a11y-fix') 
						? ( 
							<Box aria-hidden="true">
								<LinkExternalIcon
									label={formatMessage(messages.linkExternalIconLabel)}
									size="small"
									spacing="spacious"
								/>
							</Box>
						)
						: (
							<LinkExternalIcon
								label={formatMessage(messages.linkExternalIconLabel)}
								size="small"
								spacing="spacious"
							/>
						)
				)
			}
			href={url || ''}
			target={openInSameTab ? '_self' : '_blank'}
			onClick={onClick}
			ref={mergedRefs}
			isSelected={isSelected}
		>
			<span aria-label={ariaLabel}>{labelElem ?? label}</span>
		</MenuLinkItemPrimitive>
	);
};

const MenuLinkItemDraggableComponent = ({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	isReorderable,
	optionalRef,
	menuItemId,
	openInSameTab,
	optionsMenuElem,
	analyticsName,
	isSelected,
}: MenuLinkItemDraggableProps) => {
	const { formatMessage } = useIntl();
	const { enabled } = useDragAndDropOptions();
	const { draggableRef, isDragging, maybeDragPreview, dropTargetRef, dropIndicator } =
		useL1ItemDragAndDrop({
			id: menuItemId,
			isExpandable: false,
			isExpandOrFlyoutOpen: false,
			l1DragAndDropEnabled: enabled,
			onExpandFlyoutToggle: () => {},
			peek: false,
			item: {
				type: 'link',
				elemBefore: icon,
				label,
				labelElem,
				href: url || '',
				isReorderable,
			},
			group: L1_MENU_GROUPS.SHORTCUTS,
		});

	// Can possibly be undefined
	const draggableRefCollection = draggableRef ? [draggableRef] : [];
	const mergedRefs = mergeRefs(
		optionalRef ? [optionalRef, ...draggableRefCollection] : [...draggableRefCollection],
	);
	const shouldShowActionsAndOptions = getShouldShowActionsAndOptions(actions, optionsMenuElem);

	return (
		<>
			<MenuLinkItemPrimitive
				isDragging={isDragging}
				hasDragIndicator
				ref={mergedRefs}
				dropIndicator={dropIndicator}
				visualContentRef={dropTargetRef}
				actionsOnHover={
					shouldShowActionsAndOptions && (
						<ActionsAndOptions
							actions={actions}
							analyticsName={analyticsName}
							optionsMenuElem={optionsMenuElem}
						/>
					)
				}
				elemBefore={icon}
				elemAfter={
					!openInSameTab && (
						fg('navx-4022-gas-icons-a11y-fix') 
							? ( 
								<Box aria-hidden="true">
									<LinkExternalIcon
										label={formatMessage(messages.linkExternalIconLabel)}
										size="small"
										spacing="spacious"
									/>
								</Box>
							)
							: (
								<LinkExternalIcon
									label={formatMessage(messages.linkExternalIconLabel)}
									size="small"
									spacing="spacious"
								/>
							)
					)
				}
				href={url || ''}
				target={openInSameTab ? '_self' : '_blank'}
				isSelected={isSelected}
				onClick={onClick}
			>
				<span aria-label={ariaLabel}>{labelElem ?? label}</span>
			</MenuLinkItemPrimitive>
			{maybeDragPreview}
		</>
	);
};

export const MenuLinkItem = ({
	analyticsName,
	url,
	label,
	labelElem,
	icon,
	disabled,
	onDisable,
	ariaLabel,
	loading,
	actions,
	onClick,
	onRightClick,
	onFocus,
	onMouseEnter,
	optionalRef,
	isReorderable,
	menuItemId,
	isSelected,
	product = 'globalNavigation',
	openInSameTab,
	optionsMenuElem,
}: MenuLinkItemProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { enabled } = useDragAndDropOptions();

	const analyticsUrlWrapper = useCrossProductUrlWrapper({
		bridge: 'globalShortcuts',
		product,
	});

	const getTargetUrl = useCallback(
		(url: string) => {
			// For SPA links, we don't want to wrap the URL
			if (!isValidUrl(url)) {
				return url;
			}

			try {
				const parsedUrl = new URL(url);
				const continueParam = parsedUrl.searchParams.get('continue');
				if (!continueParam) {
					// If there is no continue param, we just wrap the whole URL
					return analyticsUrlWrapper(url);
				}

				// Safely deserialize identity links e.g: https://id.atlassian.com/login?login_hint=...
				const wrappedContinueUrl = analyticsUrlWrapper(continueParam);
				// Reconstruct the original URL with the wrapped continue param
				parsedUrl.searchParams.set('continue', wrappedContinueUrl);
				return parsedUrl.toString();
			} catch (e) { 
				// eslint-disable-line @typescript-eslint/no-unused-vars
				// Gracefully handle any URL parsing errors by returning the original URL
				// TODO: Log error to Sentry
				return url;
			}
		},
		[analyticsUrlWrapper],
	);

	const onClickMenuLinkItem = useCallback(
		(event?: React.MouseEvent | React.KeyboardEvent) => {
			fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
			onClick?.(event);
		},
		[createAnalyticsEvent, analyticsName, onClick],
	);

	// If loading is true, we want to render a representation of the button
	if (loading === true) {
		return <MenuLoadingItem icon={icon} label={labelElem ?? label} showLinkIcon />;
	}

	// Don't render anything if url is undefined or empty, and disabled is not explicitly set to true
	if ((!url || url === '') && !disabled) {
		return null;
	}

	// Render disabled items if no url and/or disabled is true
	if (!url || disabled) {
		if (!url || url === '') {
			onDisable?.('Invalid URL');
		} else {
			onDisable?.();
		}

		return (
			<ButtonMenuItem elemBefore={icon} isDisabled>
				{labelElem ?? label}
				<span aria-label={formatMessage(messages.disabledItemAriaLabel)} />
			</ButtonMenuItem>
		);
	}

	const targetUrl = getTargetUrl(url);

	if (fg('teams-shortcut-next-confluence')) {
		return (
			<ElementEventListener
				onRightClick={onRightClick}
				onMouseEnter={onMouseEnter}
				onFocus={onFocus}
			>
				{enabled && menuItemId ? (
					<MenuLinkItemDraggableComponent
						optionalRef={optionalRef}
						url={targetUrl}
						icon={icon}
						ariaLabel={ariaLabel}
						label={label}
						labelElem={labelElem}
						actions={actions}
						onClick={onClickMenuLinkItem}
						isReorderable={isReorderable}
						isSelected={isSelected}
						menuItemId={menuItemId}
						openInSameTab={openInSameTab}
					/>
				) : (
					<MenuLinkItemComponent
						optionalRef={optionalRef}
						url={targetUrl}
						icon={icon}
						ariaLabel={ariaLabel}
						label={label}
						labelElem={labelElem}
						actions={actions}
						onClick={onClickMenuLinkItem}
						isReorderable={isReorderable}
						isSelected={isSelected}
						openInSameTab={openInSameTab}
					/>
				)}
			</ElementEventListener>
		);
	}

	return (
		<ElementEventListener handler={onRightClick}>
			{enabled && menuItemId ? (
				<MenuLinkItemDraggableComponent
					optionalRef={optionalRef}
					url={targetUrl}
					icon={icon}
					ariaLabel={ariaLabel}
					label={label}
					labelElem={labelElem}
					actions={actions}
					onClick={onClickMenuLinkItem}
					isReorderable={isReorderable}
					menuItemId={menuItemId}
					isSelected={isSelected}
					openInSameTab={openInSameTab}
					optionsMenuElem={optionsMenuElem}
					analyticsName={analyticsName}
				/>
			) : (
				<MenuLinkItemComponent
					optionalRef={optionalRef}
					url={targetUrl}
					icon={icon}
					ariaLabel={ariaLabel}
					label={label}
					labelElem={labelElem}
					actions={actions}
					onClick={onClickMenuLinkItem}
					isReorderable={isReorderable}
					isSelected={isSelected}
					openInSameTab={openInSameTab}
					optionsMenuElem={optionsMenuElem}
					analyticsName={analyticsName}
				/>
			)}
		</ElementEventListener>
	);
};

const isValidUrl = (urlString: unknown) => {
	if (typeof urlString !== 'string') {
		return false;
	}
	try {
		return Boolean(new URL(urlString));
	} catch (e) { 
		// eslint-disable-line @typescript-eslint/no-unused-vars
		return false;
	}
};
